@if (cards().length) {
  <div class="flex flex-col gap-4 flex-1 overflow-y-auto">
    @if (pinnedEnvelopeLabel()) {
      <div class="flex items-center gap-2 shrink-0 px-1 pt-1">
        <p-message severity="success" size="small" [closable]="true" (onClose)="clearEnvelopeFilter()">
          Showing {{ pinnedEnvelopeLabel() }}
        </p-message>
      </div>
    }

    @for (card of cards(); track card.envelope.dealDocumentId) {
      <div class="flex flex-col border border-slate-200 rounded-xl bg-white shrink-0 overflow-hidden">
        <!-- Envelope header -->
        <div class="flex flex-wrap items-center gap-3 p-4 bg-surface-50">
          <p-button
            icon="pi pi-envelope"
            [plain]="true"
            [raised]="false"
            [outlined]="true"
            [rounded]="false"
            size="small"
            styleClass="!w-8 !h-8 !p-0 !rounded-[20px] shrink-0"
            [attr.aria-hidden]="true"
            [tabindex]="-1"
          />
          <div class="flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap leading-none">
              <h3 class="m-0 text-body-600 text-surface-800 leading-none">{{ card.label }}</h3>
              @if (card.flowTag) {
                <p-tag [value]="card.flowTag" size="small" severity="info" styleClass="uppercase" />
              }
              @if (card.statusTag) {
                <p-tag
                  [value]="card.statusTag.label"
                  size="small"
                  [severity]="card.statusTag.severity"
                  styleClass="uppercase"
                />
              }
            </div>
            <span class="text-body-xs text-surface-500">
              {{ card.envelope.addendumCount }} Addendum{{ card.envelope.addendumCount === 1 ? '' : 's' }} ·
              <span class="text-body-xs-600 text-emerald-700">{{ card.envelope.totalAmount | currencyFormat }}</span>
            </span>
          </div>
          <div class="flex items-center gap-2 ml-auto flex-wrap justify-end">
            @if (card.envelope.canVoid) {
              <p-button
                label="Void Envelope"
                severity="danger"
                [outlined]="true"
                [rounded]="true"
                size="small"
                (onClick)="openVoidDialog(card.envelope)"
              />
            }
            <p-button
              label="View PDF"
              [outlined]="true"
              [rounded]="true"
              size="small"
              [loading]="card.isDownloading"
              (onClick)="downloadPdf(card.envelope)"
            />
          </div>
        </div>

        <!-- Sub tabs, local to this card -->
        <div class="p-4 pb-0 shrink-0">
          <p-selectbutton
            class="dsm-tab-toggle !inline-flex !w-auto"
            [options]="subTabOptions"
            optionLabel="label"
            optionValue="value"
            [ngModel]="card.activeTab"
            [allowEmpty]="false"
            (onChange)="selectTab(card.envelope.dealDocumentId, $event.value)"
          />
        </div>

        <div class="flex flex-col p-4">
          @if (card.showSignersTab) {
            <dsm-aps-sign-off-signers-list [envelope]="card.envelope" />
          } @else {
            <dsm-aps-sign-off-addendums [addendums]="card.addendums" />
          }
        </div>

        @if (card.hasUpdatedAddendums) {
          <div class="flex items-center gap-2 px-4 pb-4 shrink-0 text-body-xs text-red-600">
            <i class="pi pi-exclamation-circle text-body-sm"></i>
            <span>Some Addendums in this envelope have been updated.</span>
          </div>
        }
      </div>
    }
  </div>
} @else if (isLoading()) {
  <div class="flex items-center justify-center flex-1 p-8 text-body-sm text-surface-500">Loading envelopes…</div>
} @else {
  <div class="flex flex-col items-center justify-center flex-1 gap-2 p-8 text-center">
    <i class="pi pi-envelope text-2xl text-surface-400"></i>
    <p class="m-0 text-body-sm text-surface-500">
      No envelope yet. Send addendums for sign-off from the Addendums tab to start tracking signatures.
    </p>
  </div>
}

<p-dialog
  header="Void Envelope"
  [visible]="!!voidTarget()"
  (visibleChange)="!$event && closeVoidDialog()"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: 'min(94vw, 1024px)' }"
  [contentStyle]="{ overflow: 'auto', maxHeight: '70vh' }"
>
  <div class="flex flex-col gap-4">
    <p class="m-0 text-body-sm text-surface-500">
      The following addendum(s) are part of this envelope. If you wish to Void the envelope click "Void Envelope".
      Otherwise "Cancel" to go back.
    </p>

    @if (isVoidPreviewLoading()) {
      <span class="inline-flex items-center gap-2 text-body-xs text-surface-500">
        <i class="pi pi-spin pi-spinner text-body-2xs"></i>
        Checking which addendums this envelope covers…
      </span>
    }

    @if (voidBlockReason()) {
      <span class="inline-flex items-start gap-1 text-body-xs text-red-600">
        <i class="pi pi-exclamation-circle text-body-2xs mt-0.5"></i>
        {{ voidBlockReason() }}
      </span>
    }

    <dsm-sign-off-addendum-list [addendums]="voidAddendums()" />

    <div class="flex flex-col gap-1">
      <generic-dropdown
        [label]="'Void reason'"
        [required]="true"
        placeholder="Select void reason"
        [items]="voidReasonsDropdownItems()"
        [ngModel]="selectedVoidReasonId()"
        (ngModelChange)="selectedVoidReasonId.set($event ?? ''); showVoidReasonError.set(false)"
        [ngModelOptions]="{ standalone: true }"
      />
    </div>

    @if (voidReasonRequiresComment()) {
      <div class="flex flex-col gap-1">
        <label class="text-body-sm text-surface-600" for="voidComment">
          Additional comments <span class="text-red-600">*</span>
        </label>
        <textarea
          id="voidComment"
          pTextarea
          rows="3"
          class="w-full"
          placeholder="Why is this envelope being voided?"
          [ngModel]="voidComment()"
          (ngModelChange)="voidComment.set($event); showVoidReasonError.set(false)"
        ></textarea>
      </div>
    }

    @if (showVoidReasonError()) {
      <span class="inline-flex items-start gap-1 text-body-xs text-red-600">
        <i class="pi pi-exclamation-circle text-body-2xs mt-0.5"></i>
        {{ voidReasonRequiresComment() ? 'Comment is required.' : 'Please select a void reason.' }}
      </span>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-2 w-full">
      <p-button label="Cancel" severity="secondary" type="button" (onClick)="closeVoidDialog()" />
      <p-button
        label="Void Envelope"
        severity="danger"
        type="button"
        [disabled]="!canConfirmVoid()"
        [loading]="isVoiding()"
        (onClick)="confirmVoidEnvelope()"
      />
    </div>
  </ng-template>
</p-dialog>
